import { UntypedFormControl, UntypedFormGroup, UntypedFormArray, NG_VALUE_ACCESSOR, ReactiveFormsModule, FormGroup } from '@angular/forms';
import * as i0 from '@angular/core';
import { Injectable, Pipe, forwardRef, Input, ViewEncapsulation, Component, InjectionToken, NgModule } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import { isEmptyObject, EMAIL_REGEX, CITY_REGEX, US_ZIP_REGEX, charCountMatchingRegex, stripHTML } from '@engineering11/utility';
import { parse, isValid } from 'date-fns';

class Validation {
    static validateAll(formGroup) {
        Object.keys(formGroup.controls).forEach((field) => {
            const control = formGroup.get(field);
            if (control instanceof UntypedFormControl) {
                control.markAsTouched({ onlySelf: true });
                control.markAsDirty();
                control.updateValueAndValidity();
            }
            else if (control instanceof UntypedFormGroup) {
                this.validateAll(control);
            }
            else if (control instanceof UntypedFormArray) {
                for (const con of control.controls) {
                    this.validateAll(con);
                }
            }
        });
    }
}

var FormError;
(function (FormError) {
    FormError["invalidEmail"] = "invalidEmail";
    FormError["invalidPassword"] = "invalidPassword";
    FormError["hasNumberError"] = "hasNumberError";
    FormError["hasSpecialCharacterError"] = "hasSpecialCharacterError";
    FormError["hasWhitespaceError"] = "hasWhitespaceError";
    FormError["hasAlphaNumericError"] = "hasAlphaNumericError";
    FormError["isNotNumeric"] = "isNotNumeric";
    FormError["isPositiveNumber"] = "isPositiveNumber";
    FormError["invalidNameError"] = "invalidNameError";
    FormError["invalidPhoneNumberError"] = "invalidPhoneNumberError";
    FormError["invalidAddressCityError"] = "invalidAddressCityError";
    FormError["invalidZipCodeError"] = "invalidZipCodeError";
    FormError["invalidCountryError"] = "invalidCountryError";
    FormError["invalidWebsiteError"] = "invalidWebsiteError";
    FormError["invalidMonthError"] = "invalidMonthError";
    FormError["inBlacklistError"] = "inBlacklistError";
    FormError["notInWhitelistError"] = "notInWhitelistError";
    FormError["minLengthAlphanumericError"] = "minLengthAlphanumericError";
    FormError["minLengthAlphabeticalError"] = "minLengthAlphabeticalError";
    FormError["maxlength"] = "maxlength";
})(FormError || (FormError = {}));
const DEFAULT_ERROR_MESSAGE = "This doesn't look right, please confirm";
// TODO: Add better custom copy
/**
 * These should remain short enough for mobile devices
 */
const FormErrorMessages = {
    [FormError.hasAlphaNumericError]: 'Only letters and numbers',
    [FormError.hasNumberError]: DEFAULT_ERROR_MESSAGE,
    [FormError.hasSpecialCharacterError]: DEFAULT_ERROR_MESSAGE,
    [FormError.invalidEmail]: DEFAULT_ERROR_MESSAGE,
    [FormError.invalidPassword]: DEFAULT_ERROR_MESSAGE,
    [FormError.hasWhitespaceError]: DEFAULT_ERROR_MESSAGE,
    [FormError.isNotNumeric]: DEFAULT_ERROR_MESSAGE,
    [FormError.isPositiveNumber]: 'Only positive numbers',
    [FormError.invalidNameError]: 'No numbers or special characters',
    [FormError.invalidPhoneNumberError]: DEFAULT_ERROR_MESSAGE,
    [FormError.invalidAddressCityError]: DEFAULT_ERROR_MESSAGE,
    [FormError.invalidZipCodeError]: 'Is that right? (e.g. 00123)',
    [FormError.invalidCountryError]: DEFAULT_ERROR_MESSAGE,
    [FormError.invalidWebsiteError]: DEFAULT_ERROR_MESSAGE,
    [FormError.invalidMonthError]: DEFAULT_ERROR_MESSAGE,
    [FormError.inBlacklistError]: DEFAULT_ERROR_MESSAGE,
    [FormError.notInWhitelistError]: DEFAULT_ERROR_MESSAGE,
    [FormError.minLengthAlphanumericError]: DEFAULT_ERROR_MESSAGE,
    [FormError.minLengthAlphabeticalError]: DEFAULT_ERROR_MESSAGE,
    [FormError.maxlength]: "{{fieldName}}'s maximum length is {{requiredLength}} characters",
};

// If we encounter a type of error without copy provided, fall back on this text
const DEFAULT_ERROR_FALLBACK = "This doesn't look right, please confirm";
const DEFAULT_ERRORS = {
    ...FormErrorMessages,
    // Basic Angular validators
    min: "{{fieldName}}'s minimum value is {{min}}",
    max: "{{fieldName}}'s maximum value is {{max}}",
    required: 'This is a required field',
    requiredTrue: '{{fieldName}} is a required field',
    // Required custom
    requiredAuthCode: 'Please enter your supplied authorization code.',
    enterEmail: 'Please enter a valid email address',
    minLength: "{{fieldName}}'s minimum length is {{requiredLength}} characters",
    maxLength: "{{fieldName}}'s maximum length is {{requiredLength}} characters",
    pattern: DEFAULT_ERROR_FALLBACK,
    // invalid custom
    invalidEmail: 'Please enter a valid email',
    invalidAuthCode: "That doesn't appear to be a valid authorization code.",
    invalidCompanyAddress: "Please enter your company's primary/main address.",
    // valid exmaples
    validPostalCodeExample: 'Ex: 12345-1111',
    unacceptedTerms: 'You must accept the terms before you can continue.',
};

class E11InputErrorMappingService {
    static { this.errorMessages = DEFAULT_ERRORS; }
    constructor() { }
    static getErrorMessages() {
        return this.errorMessages;
    }
    static addErrorMessages(errorsMapping) {
        this.errorMessages = { ...this.errorMessages, ...errorsMapping };
        return this;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorMappingService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorMappingService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorMappingService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

const ucFirst = (str) => {
    if (typeof str !== 'string' || str.length > 2) {
        return str;
    }
    const firstLetter = str.substr(0, 1);
    return firstLetter.toUpperCase() + str.substr(1);
};
class FormatStringPipe {
    transform(value, args) {
        for (var key in args) {
            value = value.replace("{{" + key + "}}", args[key]);
        }
        return ucFirst(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FormatStringPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: FormatStringPipe, name: "formatString" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: FormatStringPipe, decorators: [{
            type: Pipe,
            args: [{ name: "formatString" }]
        }] });

class E11InputErrorsComponent {
    constructor() {
        this.parentForm = new UntypedFormGroup({});
        this.label = 'This field';
        this.formSubmitted = false;
        this.suppressError = false; // Useful if needing to suppress default error functionality
        this.destroy$ = new Subject();
        this.active = false;
        this.message = '';
        this.messages = {};
    }
    ngOnInit() {
        const globalMessages = E11InputErrorMappingService.getErrorMessages();
        this.messages = { ...globalMessages, ...this.messageOverrides };
        if ((this.parentForm?.dirty && this.formControl?.invalid && this.formControl.dirty) || (this.formSubmitted && this.formControl?.invalid)) {
            this.validate();
        }
        this.formControl?.statusChanges?.pipe(takeUntil(this.destroy$))?.subscribe((status) => {
            this.validate();
        });
    }
    validate() {
        if (!this.formControl.errors || !Object.keys(this.formControl.errors).length || this.suppressError) {
            return this.clearError();
        }
        let key = Object.keys(this.formControl.errors)[0];
        this.active = true;
        this.message = this.messages[key] ?? DEFAULT_ERROR_FALLBACK;
        this.messageTransformation = { fieldName: this.label, fieldValue: this.formControl.value };
        if (typeof this.formControl.errors[key] === 'object') {
            this.messageTransformation = {
                ...this.messageTransformation,
                ...this.formControl.errors[key],
            };
        }
    }
    ngOnDestroy() {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }
    writeValue(obj) { }
    registerOnChange(fn) { }
    registerOnTouched(fn) { }
    clearError() {
        this.active = false;
        this.message = '';
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InputErrorsComponent, selector: "e11-input-errors", inputs: { formControl: "formControl", parentForm: "parentForm", label: "label", formSubmitted: "formSubmitted", suppressError: "suppressError", messageOverrides: "messageOverrides" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11InputErrorsComponent),
                multi: true,
            },
        ], ngImport: i0, template: "<!-- <div class=\"e11-my-2 e11-text-sm e11-text-skin-error\" [ngClass]=\"{ 'e11-opacity-100': active }\">\n  {{ message | formatString: messageTransformation }}\n</div> -->\n\n<div class=\"e11-error-messages e11-inline-block e11-relative e11-h-7\">\n  <span\n    class=\"e11-error-message e11-absolute e11-text-skin-error e11-text-sm\"\n    [ngClass]=\"{ 'e11-error-message-active': active }\"\n    [attr.title]=\"active ? (message | formatString: messageTransformation) : undefined\"\n  >\n    {{ message | formatString: messageTransformation }}\n  </span>\n</div>\n", styles: [".e11-error-messages{display:inline-block;width:100%;opacity:1;z-index:0;overflow:hidden}.e11-error-messages .e11-error-message{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:inherit;top:-12px;opacity:0;z-index:-1;transition:top .25s ease-in-out 0s,opacity .25s ease-in-out 0s,z-index 0s linear 0s}.e11-error-messages .e11-error-message.e11-error-message-active{top:4px;opacity:1;z-index:1;transition:top .25s ease-in-out 0s,opacity .25s ease-in-out 0s,z-index 0s linear 0s}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "pipe", type: FormatStringPipe, name: "formatString" }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-input-errors', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11InputErrorsComponent),
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, template: "<!-- <div class=\"e11-my-2 e11-text-sm e11-text-skin-error\" [ngClass]=\"{ 'e11-opacity-100': active }\">\n  {{ message | formatString: messageTransformation }}\n</div> -->\n\n<div class=\"e11-error-messages e11-inline-block e11-relative e11-h-7\">\n  <span\n    class=\"e11-error-message e11-absolute e11-text-skin-error e11-text-sm\"\n    [ngClass]=\"{ 'e11-error-message-active': active }\"\n    [attr.title]=\"active ? (message | formatString: messageTransformation) : undefined\"\n  >\n    {{ message | formatString: messageTransformation }}\n  </span>\n</div>\n", styles: [".e11-error-messages{display:inline-block;width:100%;opacity:1;z-index:0;overflow:hidden}.e11-error-messages .e11-error-message{white-space:nowrap;overflow:hidden;text-overflow:ellipsis;width:inherit;top:-12px;opacity:0;z-index:-1;transition:top .25s ease-in-out 0s,opacity .25s ease-in-out 0s,z-index 0s linear 0s}.e11-error-messages .e11-error-message.e11-error-message-active{top:4px;opacity:1;z-index:1;transition:top .25s ease-in-out 0s,opacity .25s ease-in-out 0s,z-index 0s linear 0s}\n"] }]
        }], ctorParameters: () => [], propDecorators: { formControl: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], label: [{
                type: Input
            }], formSubmitted: [{
                type: Input
            }], suppressError: [{
                type: Input
            }], messageOverrides: [{
                type: Input
            }] } });

const E11InputErrorConfigService = new InjectionToken('ErrorsMappingConfig');

class E11InputErrorsModule {
    static forRoot(config) {
        E11InputErrorMappingService.addErrorMessages(config.default ?? {});
        E11InputErrorMappingService.addErrorMessages(config.additional ?? {});
        return {
            ngModule: E11InputErrorsModule,
            providers: [
                E11InputErrorMappingService,
                {
                    provide: E11InputErrorConfigService,
                    useValue: config,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorsModule, declarations: [E11InputErrorsComponent, FormatStringPipe], imports: [CommonModule, ReactiveFormsModule], exports: [E11InputErrorsComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorsModule, imports: [CommonModule, ReactiveFormsModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InputErrorsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11InputErrorsComponent, FormatStringPipe],
                    imports: [CommonModule, ReactiveFormsModule],
                    exports: [E11InputErrorsComponent],
                }]
        }] });

function formValueValidator(...validationSpecs) {
    return (control) => {
        const value = control.value;
        const errors = validationSpecs.reduce((acc, { isValidFn, errorName, errorInfo }) => (!isValidFn(value) ? { ...acc, [errorName]: errorInfo ?? true } : acc), {});
        return isEmptyObject(errors) ? null : errors;
    };
}
const DEFAULT_MAX_LENGTH = 100; // For basic input validation
const DEFAULT_MIN_LENGTH = 1;
class E11FormValidator {
    static emailValidator() {
        const isEmailValid = (value) => !value || EMAIL_REGEX.test(value);
        return formValueValidator({ isValidFn: isEmailValid, errorName: FormError.invalidEmail }, E11FormValidator.maxLengthSpec());
    }
    static passwordValidator() {
        const PASSWORD_REGEX = new RegExp('^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9]).{8,}$');
        const isPasswordValid = (value) => !value || PASSWORD_REGEX.test(value);
        return formValueValidator({ isValidFn: isPasswordValid, errorName: FormError.invalidPassword });
    }
    static hasNumberValidator() {
        const HAS_NUMBER_REGEX = new RegExp('[0-9]');
        const hasNumber = (value) => !value || HAS_NUMBER_REGEX.test(value);
        return formValueValidator({ isValidFn: hasNumber, errorName: FormError.hasNumberError });
    }
    static hasSpecialCharacterValidator() {
        const HAS_SPECIAL_CHARACTER_REGEX = new RegExp('[^A-Za-z0-9]');
        const hasSpecialCharacter = (value) => !value || HAS_SPECIAL_CHARACTER_REGEX.test(value);
        return formValueValidator({ isValidFn: hasSpecialCharacter, errorName: FormError.hasSpecialCharacterError });
    }
    static whitespaceValidator() {
        const doesNotStartWithWhitespace = (value) => !value || !value.startsWith(' ');
        return formValueValidator({ isValidFn: doesNotStartWithWhitespace, errorName: FormError.hasWhitespaceError });
    }
    static isAlphaNumericValidator() {
        const IS_ALPHANUMERIC = new RegExp('^[ a-zA-Z0-9]+$');
        const alphaNumeric = (value) => value || IS_ALPHANUMERIC.test(value);
        return formValueValidator({ isValidFn: alphaNumeric, errorName: FormError.hasAlphaNumericError });
    }
    static isNumericValidator() {
        const numeric = (value) => !value || !isNaN(value);
        return formValueValidator({ isValidFn: numeric, errorName: FormError.isNotNumeric });
    }
    static isPositiveNumberValidator() {
        // const IS_NEGATIVE_ZERO = /^-0+$/
        const numeric = (value) => !value || value > 0;
        return formValueValidator({ isValidFn: numeric, errorName: FormError.isPositiveNumber });
    }
    static nameValidator(options) {
        const nameMaxLength = options?.maxLength ?? DEFAULT_MAX_LENGTH;
        const NAME_REGEX = new RegExp("^[A-z ,.'-]+$");
        const hasValidCharacters = (value) => !value || NAME_REGEX.test(value);
        return formValueValidator({ isValidFn: hasValidCharacters, errorName: FormError.invalidNameError }, E11FormValidator.maxLengthSpec(nameMaxLength));
    }
    static phoneValidator() {
        const PHONE_REGEX = new RegExp('^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-s./0-9]*$');
        const isPhone = (value) => !value || PHONE_REGEX.test(value);
        return formValueValidator({ isValidFn: isPhone, errorName: FormError.invalidPhoneNumberError });
    }
    static validateAddressStreet(options) {
        const { maxLength, minLengthAlphabetical } = { maxLength: DEFAULT_MAX_LENGTH, minLengthAlphabetical: DEFAULT_MIN_LENGTH, ...options };
        return formValueValidator(E11FormValidator.minLengthAlphabeticalSpec(minLengthAlphabetical), E11FormValidator.maxLengthSpec(maxLength));
    }
    static validateAddressCity() {
        const isCity = (value) => !value || CITY_REGEX.test(value);
        return formValueValidator({ isValidFn: isCity, errorName: FormError.invalidAddressCityError });
    }
    static validateAddressZip() {
        const isZip = (value) => !value || US_ZIP_REGEX.test(value);
        return formValueValidator({ isValidFn: isZip, errorName: FormError.invalidZipCodeError });
    }
    static validateAddressCountry() {
        const COUNTRY_REGEX = new RegExp('^[A-z]{2,3}$');
        const isCountry = (value) => !value || COUNTRY_REGEX.test(value);
        return formValueValidator({ isValidFn: isCountry, errorName: FormError.invalidCountryError });
    }
    static validateWebsite() {
        // const regex = new RegExp('^((https?|ftp|smtp)://)?(www.)?[a-z0-9]+.[a-z]+(/[a-zA-Z0-9#]+/?)*$')
        const WEBSITE_REGEX = new RegExp('^((?:https?://)(?:[-a-z0-9]+.)*[-a-z0-9]+.*)');
        const isWebsite = (value) => !value || WEBSITE_REGEX.test(value);
        return formValueValidator({ isValidFn: isWebsite, errorName: FormError.invalidWebsiteError });
    }
    static validateMonths() {
        const MONTHS_REGEX = new RegExp('^(0|1|2|3|4|5|6|7|8|9|10|11|12)$');
        const isMonth = (value) => !value || MONTHS_REGEX.test(value);
        return formValueValidator({ isValidFn: isMonth, errorName: FormError.invalidMonthError });
    }
    static valueNotIn(blacklist) {
        const notInBlacklist = (value) => !value || (blacklist instanceof Set ? !blacklist.has(value) : !blacklist.includes(value));
        return formValueValidator({ isValidFn: notInBlacklist, errorName: FormError.inBlacklistError, errorInfo: { blacklist } });
    }
    static valueIsIn(whitelist) {
        const inWhitelist = (value) => !value || (whitelist instanceof Set ? whitelist.has(value) : whitelist.includes(value));
        return formValueValidator({ isValidFn: inWhitelist, errorName: FormError.notInWhitelistError, errorInfo: { whitelist } });
    }
    /**
     * Has at least this many alphanumeric characters
     */
    static minLengthAlphanumeric(minLength) {
        return formValueValidator(E11FormValidator.minLengthAlphaNumericSpec(minLength));
    }
    static minLengthAlphabetical(minLength) {
        return formValueValidator(E11FormValidator.minLengthAlphabeticalSpec(minLength));
    }
    static maxLengthClean(maxLength) {
        return formValueValidator(E11FormValidator.maxLengthCleanSpec(maxLength));
    }
    /**
     * Determines if the date is parsable by our formatting library
     * @param format a format acceptable by date-fns parse method
     * @returns
     */
    static dateIsParsable(format) {
        return (control) => {
            try {
                const date = parse(control.value, format, new Date());
                if (!isValid(date))
                    return { NotParsable: true };
                return null;
            }
            catch (error) {
                return { ErrorParsing: true };
            }
        };
    }
    static dateIsPast(format) {
        return (control) => {
            const formValue = control.value;
            try {
                const date = parse(formValue, format, new Date());
                if (date > new Date())
                    return { DateFuture: true };
                return null;
            }
            catch (error) {
                return { ErrorParsing: true };
            }
        };
    }
    static emailMatch(email, emailConfirm) {
        return (formGroup) => {
            const emailControl = formGroup.controls[email];
            const emailConfirmControl = formGroup.controls[emailConfirm];
            if (!emailControl || !emailConfirmControl)
                return null;
            if (emailConfirmControl.errors && !emailConfirmControl.errors.emailMatchError)
                return null;
            if (emailControl.value !== emailConfirmControl.value) {
                emailConfirmControl.setErrors({ emailMatchError: true });
                return true;
            }
            else {
                emailConfirmControl.setErrors(null);
                return true;
            }
        };
    }
    static passwordMatch(password, passwordConfirm) {
        return (formGroup) => {
            const passwordControl = formGroup.controls[password];
            const passwordConfirmControl = formGroup.controls[passwordConfirm];
            if (!passwordControl || !passwordConfirmControl)
                return null;
            if (passwordConfirmControl.errors && !passwordConfirmControl.errors.passwordMatchError)
                return null;
            if (passwordControl.value !== passwordConfirmControl.value) {
                passwordConfirmControl.setErrors({ passwordMatchError: true });
                return true;
            }
            else {
                passwordConfirmControl.setErrors(null);
                return true;
            }
        };
    }
    static { this.conditionalRequiredValidator = (control) => {
        if (control && control instanceof FormGroup) {
            let isAnyFieldFilled = false;
            let errors = {};
            Object.keys(control.controls).forEach((key) => {
                const fieldControl = control.controls[key];
                if (fieldControl.value) {
                    isAnyFieldFilled = true;
                }
            });
            if (isAnyFieldFilled) {
                Object.keys(control.controls).forEach((key) => {
                    const fieldControl = control.controls[key];
                    if (!fieldControl.value && !fieldControl.errors?.required) {
                        errors[key] = { required: true };
                    }
                });
                if (Object.keys(errors).length === 0) {
                    errors = null;
                }
            }
            else {
                errors = null;
            }
            return errors;
        }
        return null;
    }; }
    static minLengthAlphaNumericSpec(minLength = DEFAULT_MIN_LENGTH) {
        const ALPHANUMERIC_REGEX = new RegExp('[a-zA-Z0-9]');
        const isMinLengthAlphanumeric = (value) => !value || charCountMatchingRegex(ALPHANUMERIC_REGEX, value) >= minLength;
        return { isValidFn: isMinLengthAlphanumeric, errorName: FormError.minLengthAlphanumericError };
    }
    static minLengthAlphabeticalSpec(minLength = DEFAULT_MIN_LENGTH) {
        const ALPHABETICAL_REGEX = new RegExp('[a-zA-Z]');
        const isMinLengthAlphabetical = (value) => !value || charCountMatchingRegex(ALPHABETICAL_REGEX, value) >= minLength;
        return { isValidFn: isMinLengthAlphabetical, errorName: FormError.minLengthAlphabeticalError };
    }
    static maxLengthSpec(maxLength = DEFAULT_MAX_LENGTH) {
        const isValidLength = (value) => !value || value.length <= maxLength;
        return { isValidFn: isValidLength, errorName: FormError.maxlength, errorInfo: { requiredLength: maxLength } };
    }
    static maxLengthCleanSpec(maxLength = DEFAULT_MAX_LENGTH) {
        const isValidCleanLength = (value) => !value || stripHTML(value).replace(/&nbsp;/g, ' ').length <= maxLength;
        return { isValidFn: isValidCleanLength, errorName: FormError.maxlength, errorInfo: { requiredLength: maxLength } };
    }
}
/**
 * @deprecated use static E11FormValidator - removes need for DI to declare form validation
 */
class E11FormValidationService {
    constructor() { }
    emailValidator() {
        return E11FormValidator.emailValidator();
    }
    emailMatch(email, emailConfirm) {
        return E11FormValidator.emailMatch(email, emailConfirm);
    }
    passwordValidator() {
        return E11FormValidator.passwordValidator();
    }
    hasNumberValidator() {
        return E11FormValidator.hasNumberValidator();
    }
    hasSpecialCharacterValidator() {
        return E11FormValidator.hasSpecialCharacterValidator();
    }
    whitespaceValidator() {
        return E11FormValidator.whitespaceValidator();
    }
    isAlphaNumericValidator() {
        return E11FormValidator.isAlphaNumericValidator();
    }
    isNumericValidator() {
        return E11FormValidator.isNumericValidator();
    }
    isPositiveNumberValidator() {
        return E11FormValidator.isPositiveNumberValidator();
    }
    passwordMatch(password, passwordConfirm) {
        return E11FormValidator.passwordMatch(password, passwordConfirm);
    }
    nameValidator() {
        return E11FormValidator.nameValidator();
    }
    userNameValidator(userControl) {
        return new Promise((resolve) => {
            setTimeout(() => {
                if (this.validateUserName(userControl.value)) {
                    resolve({ userNameNotAvailable: true });
                }
                else {
                    resolve(null);
                }
            }, 1000);
        });
    }
    validateUserName(userName) {
        const UserList = ['ankit', 'admin', 'user', 'superuser'];
        return UserList.indexOf(userName) > -1;
    }
    phoneValidator() {
        return E11FormValidator.phoneValidator();
    }
    validateAddressCity() {
        return E11FormValidator.validateAddressCity();
    }
    validateAddressZip() {
        return E11FormValidator.validateAddressZip();
    }
    validateAddressCountry() {
        return E11FormValidator.validateAddressCountry();
    }
    validateWebsite() {
        return E11FormValidator.validateWebsite();
    }
    validateMonths() {
        return E11FormValidator.validateMonths();
    }
    valueNotIn(blacklist) {
        return E11FormValidator.valueNotIn(blacklist);
    }
    valueIsIn(whitelist) {
        return E11FormValidator.valueIsIn(whitelist);
    }
    dateLessThan(dateField1, dateField2, validatorField) {
        return (c) => {
            const date1 = c.get(dateField1)?.value;
            const date2 = c.get(dateField2)?.value;
            if (date1 !== null && date2 !== null && date1 > date2) {
                return validatorField;
            }
            return null;
        };
    }
    /**
     * Has at least this many alphanumeric characters
     * @param minLength
     * @returns
     */
    minLengthAlphanumeric(minLength) {
        return E11FormValidator.minLengthAlphanumeric(minLength);
    }
    maxLength(maxLength, currentLength, validatorField) {
        return (c) => {
            if (currentLength > maxLength) {
                return validatorField;
            }
            return null;
        };
    }
    /**
     * Determines if the date is parsable by our formatting library
     * @param format a format acceptable by date-fns parse method
     * @returns
     */
    dateIsParsable(format) {
        return E11FormValidator.dateIsParsable(format);
    }
    dateIsPast(format) {
        return E11FormValidator.dateIsPast(format);
    }
    notPastDateValidator(control) {
        const [year, month, day] = (control.value || '').split('-');
        const selectedDate = new Date(+year, +month - 1, +day);
        const currentDate = new Date();
        // Set the time portion of the dates to midnight to ignore time
        selectedDate.setHours(0, 0, 0, 0);
        currentDate.setHours(0, 0, 0, 0);
        if (selectedDate < currentDate) {
            return { notPastDate: true };
        }
        else {
            return null;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FormValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FormValidationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11FormValidationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [] });
/**
 * @deprecated use E11FormValidator
 */
class FormCustomValidationService extends E11FormValidationService {
}

/**
 * Generated bundle index. Do not edit.
 */

export { DEFAULT_ERROR_MESSAGE, E11FormValidationService, E11FormValidator, E11InputErrorsComponent, E11InputErrorsModule, FormCustomValidationService, FormError, FormErrorMessages, Validation };
